import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { timeAgo } from '../../lib/et.js';
import { css, html, nothing } from '../../lib/lit.js';
import { router } from '../../lib/router.js';

/** Where a new plan's issues go unless changed — the VTT is the first user. */
const DEFAULT_REPO = 'dice-driven/d3-vtt';

// Exploratory-testing plans: the list, and creating one. Plans, results and
// claims live in the Staff API's own database; agents edit the same plans over
// MCP.
class ETPlansView extends BaseComponent {
    static properties = {
        /** @type {Array<object>} */
        _plans: { state: true },
        _loading: { state: true },
        _error: { state: true },
        _creating: { state: true },
        _name: { state: true },
        _defaultRepo: { state: true },
        _description: { state: true },
        _saving: { state: true },
        _formError: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 60rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        .form-col {
            max-width: 32rem;
        }
    `;

    constructor() {
        super();
        this._plans = [];
        this._loading = true;
        this._error = '';
        this._creating = false;
        this._name = '';
        this._defaultRepo = DEFAULT_REPO;
        this._description = '';
        this._saving = false;
        this._formError = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._error = '';
        try {
            const body = await api.get('/et/plans');
            this._plans = body.plans;
        } catch (error) {
            this._error = error.message;
        } finally {
            this._loading = false;
        }
    }

    async _create(event) {
        event.preventDefault();
        this._saving = true;
        this._formError = '';
        try {
            const plan = await api.post('/et/plans', {
                name: this._name.trim(),
                defaultRepo: this._defaultRepo.trim(),
                description: this._description.trim(),
            });
            router.navigate(`/testing/plans/${plan.id}`);
        } catch (error) {
            this._formError = error.message;
        } finally {
            this._saving = false;
        }
    }

    render() {
        return html`
            <div class="wrap">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <h1 class="h4 mb-0">Exploratory testing</h1>
                    <button
                        class="btn btn-sm btn-primary ms-auto"
                        type="button"
                        ?disabled=${this._creating}
                        @click=${() => {
                            this._creating = true;
                        }}
                    >
                        <i class="bi bi-plus-lg"></i> New plan
                    </button>
                </div>
                <p class="text-muted">
                    Plans of one-line things to go and try. Pick what has gone longest untested,
                    claim the section, record what you find.
                </p>
                ${this._creating ? this._renderForm() : nothing} ${this._renderList()}
            </div>
        `;
    }

    _renderForm() {
        return html`
            <form class="card card-body mb-4 form-col" @submit=${this._create}>
                <div class="mb-3">
                    <label class="form-label" for="name">Name</label>
                    <input
                        id="name"
                        class="form-control"
                        required
                        maxlength="100"
                        placeholder="VTT"
                        .value=${this._name}
                        @input=${(e) => {
                            this._name = e.target.value;
                        }}
                    />
                </div>
                <div class="mb-3">
                    <label class="form-label" for="repo">Default repo for issues</label>
                    <input
                        id="repo"
                        class="form-control"
                        required
                        .value=${this._defaultRepo}
                        @input=${(e) => {
                            this._defaultRepo = e.target.value;
                        }}
                    />
                </div>
                <div class="mb-3">
                    <label class="form-label" for="description">Description</label>
                    <textarea
                        id="description"
                        class="form-control"
                        rows="2"
                        .value=${this._description}
                        @input=${(e) => {
                            this._description = e.target.value;
                        }}
                    ></textarea>
                </div>
                ${
                    this._formError
                        ? html`<div class="alert alert-danger" role="alert">${this._formError}</div>`
                        : nothing
                }
                <div class="d-flex gap-2">
                    <button class="btn btn-primary" type="submit" ?disabled=${this._saving}>
                        ${this._saving ? 'Creating…' : 'Create plan'}
                    </button>
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        @click=${() => {
                            this._creating = false;
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        `;
    }

    _renderList() {
        if (this._error) {
            return html`<div class="alert alert-danger" role="alert">${this._error}</div>`;
        }
        if (this._loading) {
            return html`<p class="text-muted">Loading…</p>`;
        }
        if (this._plans.length === 0) {
            return html`<p class="text-muted">No plans yet.</p>`;
        }
        const now = Date.now();
        return html`
            <div class="list-group">
                ${this._plans.map(
                    (plan) => html`
                        <a class="list-group-item list-group-item-action" href="/testing/plans/${plan.id}">
                            <div class="d-flex align-items-center gap-2">
                                <strong>${plan.name}</strong>
                                <span class="text-muted small">${plan.itemCount} items</span>
                                ${
                                    plan.neverTestedCount > 0
                                        ? html`<span class="badge text-bg-light border"
                                              >${plan.neverTestedCount} never tested</span
                                          >`
                                        : nothing
                                }
                                <span class="text-muted small ms-auto">
                                    ${
                                        plan.lastTestedAt
                                            ? `last tested ${timeAgo(plan.lastTestedAt, now)}`
                                            : 'not tested yet'
                                    }
                                </span>
                            </div>
                            ${
                                plan.description
                                    ? html`<div class="text-muted small">${plan.description}</div>`
                                    : nothing
                            }
                        </a>
                    `,
                )}
            </div>
        `;
    }
}

customElements.define('d3-et-plans-view', ETPlansView);
